import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { UploadCloud } from 'lucide-react';
import { ApiError } from '@/api/client';
import {
  requestUploadUrl,
  registerAttachment,
  MAX_ATTACHMENT_BYTES,
  type AttachmentEntityType,
} from '@/api/attachments';

type FileStatus = 'uploading' | 'done' | 'error';
interface FileProgress {
  id: number;
  name: string;
  status: FileStatus;
  error?: string;
}

interface AttachmentUploaderProps {
  entityType: AttachmentEntityType;
  entityId: number;
}

/**
 * Drag-drop / click-to-browse uploader. Per file: client-side size check →
 * request a SAS URL → PUT the bytes straight to blob storage → register the
 * metadata → refresh the list. Uploads run in parallel; per-file status shows
 * inline. Errors surface per file.
 */
export function AttachmentUploader({ entityType, entityId }: AttachmentUploaderProps) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const idRef = useRef(0);
  const [dragOver, setDragOver] = useState(false);
  const [items, setItems] = useState<FileProgress[]>([]);

  const patchItem = (id: number, patch: Partial<FileProgress>) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const dropItemLater = (id: number) =>
    setTimeout(() => setItems((prev) => prev.filter((it) => it.id !== id)), 3000);

  const uploadOne = async (file: File) => {
    const id = ++idRef.current;
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setItems((prev) => [...prev, { id, name: file.name, status: 'error', error: `${file.name} exceeds the 25 MB limit.` }]);
      return;
    }
    setItems((prev) => [...prev, { id, name: file.name, status: 'uploading' }]);
    const contentType = file.type || 'application/octet-stream';

    try {
      const { UploadUrl, BlobUri } = await requestUploadUrl({
        EntityType: entityType,
        EntityId: entityId,
        FileName: file.name,
        ContentType: contentType,
        SizeBytes: file.size,
      });

      const put = await fetch(UploadUrl, {
        method: 'PUT',
        headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': contentType },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);

      await registerAttachment({
        EntityType: entityType,
        EntityId: entityId,
        FileName: file.name,
        ContentType: contentType,
        SizeBytes: file.size,
        BlobUri,
      });

      await queryClient.invalidateQueries({ queryKey: ['attachments', { entityType, entityId }] });
      patchItem(id, { status: 'done' });
      dropItemLater(id);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Upload failed.';
      patchItem(id, { status: 'error', error: msg });
    }
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    void Promise.all(Array.from(files).map(uploadOne));
  };

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        data-testid="attachment-dropzone"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        className={
          'flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed px-4 py-6 text-center text-sm ' +
          (dragOver ? 'border-orange bg-orange/5 text-orange' : 'border-border text-darkGrey/60 hover:border-navy')
        }
      >
        <UploadCloud size={20} aria-hidden className="mb-1" />
        Drop files here, or click to browse
        <span className="mt-0.5 text-[0.7rem] text-darkGrey/40">Up to 25 MB per file</span>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        aria-label="Upload files"
        className="hidden"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
      />

      {items.length > 0 && (
        <ul className="mt-2 space-y-1">
          {items.map((it) => (
            <li key={it.id} className="flex items-center justify-between gap-2 text-xs">
              <span className="truncate text-darkGrey/70">{it.name}</span>
              {it.status === 'uploading' && <span className="text-darkGrey/40">Uploading…</span>}
              {it.status === 'done' && <span className="font-semibold text-green">Done</span>}
              {it.status === 'error' && <span className="text-red" role="alert">{it.error}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AttachmentUploader;
